import { useState, useRef, useEffect, useCallback } from "react";
import { useStatus, type ServiceStatus } from "@/hooks/useStatus";
import StatusDrawer from "./StatusDrawer";

const SERVICES = [
  { key: "zerodha" as const, label: "Zerodha" },
  { key: "ai" as const, label: "AI" },
  { key: "mongo" as const, label: "DB" },
  { key: "redis" as const, label: "Redis" },
  { key: "ticker" as const, label: "Ticker" },
  { key: "telegram" as const, label: "Telegram" },
];

function dotColor(state: string) {
  if (state === "green") return "#00b894";
  if (state === "yellow") return "#fdcb6e";
  return "#e17055";
}

export default function StatusCenter() {
  const { status } = useStatus();
  const [openService, setOpenService] = useState<string | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDotClick = useCallback(
    (key: string, e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (openService === key) {
        setOpenService(null);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      setAnchorRect(rect);
      setOpenService(key);
    },
    [openService]
  );

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenService(null);
    };
    if (openService) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
    return undefined;
  }, [openService]);

  const getServiceStatus = (key: string): ServiceStatus => {
    if (!status) return { state: "red", message: "Loading..." };
    return (status as any)[key] || { state: "red", message: "Unknown" };
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {SERVICES.map(({ key, label }) => {
        const svc = getServiceStatus(key);
        return (
          <div
            key={key}
            onClick={(e) => handleDotClick(key, e)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              position: "relative",
            }}
            title={`${label}: ${svc.message}`}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: dotColor(svc.state),
                display: "inline-block",
                flexShrink: 0,
                boxShadow:
                  svc.state === "green"
                    ? "0 0 4px #00b894"
                    : svc.state === "yellow"
                      ? "0 0 4px #fdcb6e"
                      : "0 0 4px #e17055",
              }}
            />
            <span
              style={{
                fontSize: 10,
                color: "var(--text-muted)",
                fontWeight: 500,
                letterSpacing: 0.3,
              }}
            >
              {label}
            </span>
          </div>
        );
      })}

      {openService && anchorRect && (
        <StatusDrawer
          service={openService}
          status={getServiceStatus(openService)}
          anchorRect={anchorRect}
          onClose={() => setOpenService(null)}
        />
      )}
    </div>
  );
}
